import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import {
  BadgeCheck, ShieldCheck, Store, Images, Share2, QrCode, Star, TrendingUp,
  MapPin, Megaphone, Check, ArrowRight, Sparkles, LayoutTemplate, Globe,
} from "lucide-react";

const PERK_ICONS = [LayoutTemplate, Images, Share2, QrCode, Star, MapPin, Megaphone, TrendingUp];

const CONTENT = {
  en: {
    badge: "Build trust. Win more buyers.",
    title: "Get verified on CritterPosts.ca",
    intro: "A verification badge tells buyers you're the real deal — and unlocks a premium profile that turns casual browsers into messages, adoptions and sales.",
    cta_primary: "Apply now",
    cta_secondary: "See what you unlock",
    unlock_title: "What you unlock",
    unlock_sub: "Every verified member gets an elaborate profile — a mini web page for your animals, facility and brand.",
    perks: [
      { t: "A mini web page", b: "A full profile with a cover banner, overlapping avatar and a sticky section menu — far more than a plain listing." },
      { t: "Photo gallery + spotlight", b: "Show up to 10 captioned photos, drag to reorder, and feature your best shot as a large hero banner." },
      { t: "One-tap sharing", b: "A polished share link generates a rich preview card, so your profile looks professional wherever it's posted." },
      { t: "Downloadable QR code", b: "A branded QR in your colours and logo — print it on flyers, signage and business cards for offline reach." },
      { t: "Reviews & ratings", b: "Collect star reviews that build social proof and help buyers choose you with confidence." },
      { t: "Business directory listing", b: "Verified businesses appear in the public directory with a tagline, website and filters buyers can search." },
      { t: "Advertising eligibility", b: "Verified businesses can book banner ads across the marketplace to reach even more buyers." },
      { t: "More visibility", b: "Badge filters on Browse let buyers surface verified members first — so trusted sellers get found faster." },
    ],
    tiers_title: "Two badges — apply for either, or both",
    tiers_sub: "Each category is reviewed independently, so you can hold both at once.",
    tiers: [
      {
        key: "seller", name: "Verified Seller", color: "#059669", soft: "#ECFDF5",
        for: "For individuals, hobbyists and private sellers who want to stand out and earn buyer trust.",
        points: ["Green Verified Seller badge on your profile & listings", "The full mini-webpage profile", "Photo gallery, spotlight, reviews & QR code", "Badge filter visibility on Browse"],
      },
      {
        key: "business", name: "Verified Business", color: "#D97706", soft: "#FFFBEB",
        for: "For breeders, shelters, kennels and registered businesses that want a professional presence.",
        points: ["Amber Verified Business badge — the top trust signal", "Everything in Verified Seller, plus:", "A listing in the public Business Directory", "Tagline + website link on your directory card", "Eligibility to book banner advertising"],
      },
    ],
    how_title: "How it works",
    how: [
      { t: "Apply in your dashboard", b: "Open the Verification card in your dashboard and submit a quick request for the Seller or Business badge." },
      { t: "We review it", b: "Our team checks your details — usually within a couple of days — and may ask for a document for businesses." },
      { t: "Your profile levels up", b: "Once approved, your badge appears and your mini-webpage profile tools switch on instantly." },
    ],
    faq_title: "Frequently asked",
    faq: [
      { q: "Does verification cost anything?", a: "Applying for a badge is free. Optional paid banner advertising is separate and available to verified businesses." },
      { q: "Can I have both badges?", a: "Yes. Verified Seller and Verified Business are reviewed separately and you can hold both at the same time." },
      { q: "What do I need for Verified Business?", a: "Your business name, website if you have one, and we may request a document (e.g. registration or a shelter/breeder reference) to confirm it." },
      { q: "Do badges expire?", a: "Badges are valid for a set period and renew on review — we'll remind you before yours lapses so there's no gap." },
    ],
    final_title: "Ready to stand out?",
    final_sub: "Apply in under a minute and start turning browsers into buyers.",
  },
  fr: {
    badge: "Gagnez la confiance. Attirez plus d'acheteurs.",
    title: "Faites-vous vérifier sur CritterPosts.ca",
    intro: "Un badge de vérification montre aux acheteurs que vous êtes authentique — et débloque un profil premium qui transforme les curieux en messages, adoptions et ventes.",
    cta_primary: "Postuler maintenant",
    cta_secondary: "Voir ce que vous débloquez",
    unlock_title: "Ce que vous débloquez",
    unlock_sub: "Chaque membre vérifié obtient un profil élaboré — une mini page web pour vos animaux, votre établissement et votre marque.",
    perks: [
      { t: "Une mini page web", b: "Un profil complet avec bannière de couverture, avatar superposé et un menu de sections — bien plus qu'une simple annonce." },
      { t: "Galerie photo + vedette", b: "Affichez jusqu'à 10 photos légendées, réorganisez-les et mettez votre meilleure photo en grande bannière." },
      { t: "Partage en un clic", b: "Un lien de partage soigné génère une belle carte d'aperçu — votre profil paraît professionnel partout." },
      { t: "Code QR téléchargeable", b: "Un QR à vos couleurs et logo — à imprimer sur dépliants, affiches et cartes de visite." },
      { t: "Avis et évaluations", b: "Recueillez des avis étoilés qui renforcent la preuve sociale et rassurent les acheteurs." },
      { t: "Annuaire des entreprises", b: "Les entreprises vérifiées figurent dans l'annuaire public avec slogan, site web et filtres de recherche." },
      { t: "Admissible à la publicité", b: "Les entreprises vérifiées peuvent réserver des bannières publicitaires sur tout le marché." },
      { t: "Plus de visibilité", b: "Les filtres de badge sur Parcourir mettent les membres vérifiés en avant — on vous trouve plus vite." },
    ],
    tiers_title: "Deux badges — l'un, l'autre, ou les deux",
    tiers_sub: "Chaque catégorie est examinée indépendamment : vous pouvez détenir les deux.",
    tiers: [
      {
        key: "seller", name: "Vendeur vérifié", color: "#059669", soft: "#ECFDF5",
        for: "Pour les particuliers et vendeurs privés qui veulent se démarquer et gagner la confiance.",
        points: ["Badge vert Vendeur vérifié sur votre profil et vos annonces", "Le profil mini-page web complet", "Galerie photo, vedette, avis et code QR", "Visibilité via le filtre de badge sur Parcourir"],
      },
      {
        key: "business", name: "Entreprise vérifiée", color: "#D97706", soft: "#FFFBEB",
        for: "Pour les éleveurs, refuges, chenils et entreprises enregistrées souhaitant une présence professionnelle.",
        points: ["Badge ambre Entreprise vérifiée — le signal de confiance ultime", "Tout ce qui est inclus dans Vendeur vérifié, plus :", "Une fiche dans l'annuaire public des entreprises", "Slogan + lien de site web sur votre fiche", "Admissibilité aux bannières publicitaires"],
      },
    ],
    how_title: "Comment ça marche",
    how: [
      { t: "Postulez dans votre tableau de bord", b: "Ouvrez la carte Vérification dans votre tableau de bord et envoyez une demande rapide pour le badge Vendeur ou Entreprise." },
      { t: "Nous l'examinons", b: "Notre équipe vérifie vos informations — généralement en quelques jours — et peut demander un document aux entreprises." },
      { t: "Votre profil monte en gamme", b: "Une fois approuvé, votre badge apparaît et les outils de votre mini-page web s'activent instantanément." },
    ],
    faq_title: "Questions fréquentes",
    faq: [
      { q: "La vérification est-elle payante ?", a: "Postuler pour un badge est gratuit. La publicité par bannière, optionnelle et payante, est distincte et réservée aux entreprises vérifiées." },
      { q: "Puis-je avoir les deux badges ?", a: "Oui. Vendeur vérifié et Entreprise vérifiée sont examinés séparément et vous pouvez détenir les deux." },
      { q: "Que faut-il pour Entreprise vérifiée ?", a: "Le nom de votre entreprise, votre site web le cas échéant, et nous pouvons demander un document (p. ex. enregistrement ou référence d'éleveur/refuge)." },
      { q: "Les badges expirent-ils ?", a: "Les badges sont valides pour une période donnée et se renouvellent après examen — nous vous rappelons avant l'échéance." },
    ],
    final_title: "Prêt à vous démarquer ?",
    final_sub: "Postulez en moins d'une minute et transformez les curieux en acheteurs.",
  },
};

export default function GetVerified() {
  const { lang } = useLanguage();
  const { user, setAuthOpen } = useAuth();
  const navigate = useNavigate();
  const c = CONTENT[lang] || CONTENT.en;

  const apply = () => {
    if (user) navigate("/dashboard?tab=profile");
    else setAuthOpen(true);
  };
  const scrollUnlock = () => document.getElementById("unlock")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div data-testid="get-verified-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#12234A] text-white">
        <div className="absolute inset-0 bg-gradient-to-br from-[#12234A] via-[#1E3A75] to-[#12234A]" />
        <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#F5551E]/20 blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-white bg-white/10 rounded-full px-3 py-1 backdrop-blur"><BadgeCheck size={13} className="text-[#F5551E]" /> {c.badge}</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl mt-5 tracking-tight max-w-3xl">{c.title}</h1>
          <p className="text-base sm:text-lg text-slate-200 mt-5 max-w-2xl leading-relaxed">{c.intro}</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Button onClick={apply} data-testid="getverified-hero-apply" className="rounded-xl h-12 px-7 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">{c.cta_primary} <ArrowRight size={17} /></Button>
            <Button onClick={scrollUnlock} variant="outline" data-testid="getverified-hero-see" className="rounded-xl h-12 px-7 bg-transparent border-white/30 text-white hover:bg-white/10 font-semibold">{c.cta_secondary}</Button>
          </div>
        </div>
      </section>

      {/* UNLOCK PERKS */}
      <section id="unlock" className="max-w-5xl mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#12234A] flex items-center justify-center gap-2"><Sparkles size={26} className="text-[#F5551E]" /> {c.unlock_title}</h2>
          <p className="text-slate-600 mt-3">{c.unlock_sub}</p>
        </div>

        {/* Real profile mockup */}
        <figure className="mt-10 mx-auto max-w-4xl" data-testid="getverified-mockup">
          <div className="rounded-2xl border border-slate-200 bg-slate-100 shadow-2xl overflow-hidden">
            <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-200/70 border-b border-slate-200">
              <span className="h-3 w-3 rounded-full bg-red-400" />
              <span className="h-3 w-3 rounded-full bg-amber-400" />
              <span className="h-3 w-3 rounded-full bg-green-400" />
              <span className="ml-3 flex-1 max-w-sm bg-white rounded-md text-[11px] text-slate-400 px-3 py-1 truncate">critterposts.ca/seller/golden-horizon</span>
            </div>
            <img src="/assets/verified-profile-mockup.png" alt={lang === "fr" ? "Exemple de profil vérifié" : "Example of a verified member profile"} className="w-full block" loading="lazy" />
          </div>
          <figcaption className="text-center text-sm text-slate-400 mt-3">{lang === "fr" ? "Un vrai profil vérifié sur CritterPosts.ca" : "A real verified member profile on CritterPosts.ca"}</figcaption>
        </figure>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
          {c.perks.map((p, i) => {
            const Icon = PERK_ICONS[i] || Check;
            return (
              <div key={i} data-testid={`getverified-perk-${i}`} className="rounded-2xl border border-slate-200/80 bg-white p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
                <span className="h-11 w-11 rounded-xl bg-[#FFF4F0] flex items-center justify-center text-[#F5551E]"><Icon size={21} /></span>
                <h3 className="font-display font-bold text-[#12234A] mt-3">{p.t}</h3>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{p.b}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* TIERS */}
      <section className="bg-[#F8FAFC] border-y border-slate-200/70">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#12234A]">{c.tiers_title}</h2>
            <p className="text-slate-600 mt-3">{c.tiers_sub}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            {c.tiers.map((tier) => (
              <div key={tier.key} data-testid={`getverified-tier-${tier.key}`} className="rounded-3xl border border-slate-200 bg-white p-7 flex flex-col">
                <span className="inline-flex items-center gap-1.5 text-sm font-bold px-3 py-1.5 rounded-full self-start" style={{ color: tier.color, backgroundColor: tier.soft }}>
                  <BadgeCheck size={16} /> {tier.name}
                </span>
                <p className="text-sm text-slate-500 mt-4 leading-relaxed">{tier.for}</p>
                <ul className="mt-5 space-y-2.5 flex-1">
                  {tier.points.map((pt, i) => (
                    <li key={i} className="flex gap-2.5 text-sm text-slate-700">
                      <Check size={18} className="shrink-0 mt-0.5" style={{ color: tier.color }} /> <span>{pt}</span>
                    </li>
                  ))}
                </ul>
                <Button onClick={apply} data-testid={`getverified-apply-${tier.key}`} className="rounded-xl h-11 mt-6 w-full text-white font-semibold gap-2" style={{ backgroundColor: tier.color }}>
                  {c.cta_primary} <ArrowRight size={16} />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#12234A] text-center">{c.how_title}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
          {c.how.map((s, i) => (
            <div key={i} data-testid={`getverified-step-${i}`} className="relative rounded-2xl border border-slate-200/80 bg-white p-6">
              <span className="absolute -top-4 left-6 h-9 w-9 rounded-full bg-[#F5551E] text-white font-display font-bold flex items-center justify-center shadow-md">{i + 1}</span>
              <h3 className="font-display font-bold text-[#12234A] mt-3">{s.t}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F8FAFC] border-t border-slate-200/70">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16">
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#12234A] text-center">{c.faq_title}</h2>
          <div className="mt-8 space-y-3">
            {c.faq.map((f, i) => (
              <div key={i} data-testid={`getverified-faq-${i}`} className="rounded-2xl border border-slate-200/80 bg-white p-5">
                <p className="font-semibold text-[#12234A]">{f.q}</p>
                <p className="text-sm text-slate-500 mt-1.5 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#12234A] text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 text-center">
          <ShieldCheck size={40} className="mx-auto text-[#F5551E]" />
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl mt-4">{c.final_title}</h2>
          <p className="text-slate-200 mt-3">{c.final_sub}</p>
          <Button onClick={apply} data-testid="getverified-final-apply" className="rounded-xl h-12 px-8 mt-7 bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">{c.cta_primary} <ArrowRight size={17} /></Button>
          <p className="text-xs text-slate-400 mt-6">
            <Link to="/businesses" className="hover:text-white underline-offset-2 hover:underline">{lang === "fr" ? "Voir l'annuaire des entreprises vérifiées" : "Browse the Verified Business directory"}</Link>
            {"  ·  "}
            <Link to="/trust-safety" className="hover:text-white underline-offset-2 hover:underline">{lang === "fr" ? "Confiance et sécurité" : "Trust & safety"}</Link>
          </p>
        </div>
      </section>
    </div>
  );
}
